import React, { useState } from 'react';
import { CLOUDINARY_CLOUD_NAME } from '../../../constants';
import { useCodeMaps } from '../../../utils/utils';
import { cardPlace } from '../../../utils/hometown';
import { Badge } from '../../ForBrands/ui/BrandUI';
import { Icon } from '../../ForBrands/ui/icons';
import '../../ForBrands/brandMode.css';

const DEFAULT_AVATAR = `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/default-avatar.png`;

const getConfidenceLabel = (factor) => {
  if (!factor) return '';
  const f = String(factor).toLowerCase();
  if (f === 'high' || f === 'medium' || f === 'low') {
    return f.charAt(0).toUpperCase() + f.slice(1);
  }
  return factor;
};

// "Basha High School · Chandler, AZ": the school (a code name's "(NV)"
// suffix dropped so the state isn't shown twice), then the place.
export const cardMetaLine = (school, place) =>
  [(school || '').replace(/\s*\([^)]*\)\s*$/, ''), place].filter(Boolean).join(' · ');

// The V2 score ring: the orange arc over the muted track, drawn in a 100
// unit box so it takes its container's size. No value draws the track only.
const ScoreRing = ({ value, stroke = 10, className = '', children }) => {
  const r = 50 - stroke / 2;
  const c = 2 * Math.PI * r;
  const pct = value == null ? 0 : Math.max(0, Math.min(100, Number(value) || 0)) / 100;
  return (
    <div className={`relative ${className}`}>
      <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full -rotate-90" aria-hidden="true" focusable="false">
        <circle cx="50" cy="50" r={r} fill="none" strokeWidth={stroke} className="stroke-bm-mutedbg" />
        {pct > 0 && (
          <circle
            cx="50"
            cy="50"
            r={r}
            fill="none"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={c}
            strokeDashoffset={c * (1 - pct)}
            className="stroke-bm-orange"
          />
        )}
      </svg>
      <div className="relative flex h-full w-full items-center justify-center">{children}</div>
    </div>
  );
};

const LockBadge = ({ compact }) => (
  <span
    className={`pointer-events-none absolute z-10 flex items-center justify-center rounded-full bg-white/95 text-bm-ink shadow-md backdrop-blur-sm ${
      compact ? 'left-2 top-2 h-7 w-7' : 'left-3 top-3 h-8 w-8'
    }`}
  >
    <Icon name="lock" size={compact ? 14 : 16} title="Locked" />
  </span>
);

const DISC =
  'flex aspect-square w-[56%] min-w-[170px] max-w-[250px] items-center justify-center rounded-full bg-white/85 shadow-xl ring-[6px] ring-white/50 backdrop-blur-[6px]';

// Keyboard focus shows the disc as hover does. A mouse click focuses the
// card too, so only focus the browser would ring (:focus-visible) counts;
// a browser without that selector treats every focus as keyboard focus.
const isKeyboardFocus = (el) => {
  try {
    return el.matches(':focus-visible');
  } catch {
    return true;
  }
};

// The V2 Player Index card, shared by the signed-in index (AllAthleteCard
// below reads its school and team labels) and the public index (its rows carry names).
// Phones get the photo with the CacheScore row under it; desktop gets a
// taller photo whose hover or keyboard focus shows the CacheScore disc when
// `hoverScore`. Each card's accessible name carries the score it shows.
export const PlayerIndexCard = ({
  name,
  avatar,
  alt,
  sport,
  jersey,
  meta,
  score = null,
  confidence = '',
  noScoreLabel = 'Coming soon',
  hoverScore = false,
  showLock = false,
  onClick,
}) => {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const hasScore = score != null;
  const showDisc = hoverScore && (hovered || focused);
  const photo = (avatar || DEFAULT_AVATAR).replace('/upload/', '/upload/w_400,h_600,c_fill,f_auto,q_auto/');
  const onKeyDown = (e) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick(e);
    }
  };
  // "Lleyton Hughes, CacheScore 78" when the layout shows a score.
  const cardProps = (withScore) => ({
    role: 'button',
    tabIndex: 0,
    'aria-label': withScore ? `${name}, CacheScore ${score}` : name,
    onClick,
    onKeyDown,
  });
  const focus = 'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-bm-orange focus-visible:ring-offset-2';

  // Sport, name, then jersey and "School · Place" over the photo gradient.
  const info = (compact) => (
    <div className={`absolute inset-x-0 bottom-0 z-[6] ${compact ? 'p-2.5' : 'p-5'}`}>
      {sport && (
        <Badge tone="brand" className={`uppercase ${compact ? 'mb-1' : 'mb-2'}`}>
          {sport}
        </Badge>
      )}
      <p className={`break-words text-white drop-shadow-md ${compact ? 'bm-h-s' : 'bm-h-l'}`}>{name}</p>
      {(jersey || meta) && (
        <div className={`flex min-w-0 items-center gap-2 ${compact ? 'mt-0.5' : 'mt-1.5'}`}>
          {jersey ? (
            <span className="shrink-0 rounded-full bg-white/20 px-2 py-0.5 bm-label-xs text-white">#{jersey}</span>
          ) : null}
          {meta && <span className="min-w-0 truncate bm-body-s text-white/85">{meta}</span>}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Phones: photo, then the CacheScore row */}
      <div className={`rounded-[12px] lg:hidden ${focus}`} {...cardProps(hasScore)}>
        <div className="relative aspect-[3/4] w-full overflow-hidden rounded-[12px] border border-bm-border bg-bm-mutedbg">
          <img src={photo} alt={alt} loading="lazy" className="h-full w-full object-cover" />
          {showLock && <LockBadge compact />}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[60%] bg-gradient-to-t from-black/65 via-black/20 to-transparent" />
          {info(true)}
        </div>
        <div className="mt-2.5 flex items-center gap-3 px-1">
          <ScoreRing value={hasScore ? score : null} className="h-[60px] w-[60px] shrink-0">
            {hasScore && <span className="bm-num-ml text-bm-ink">{score}</span>}
          </ScoreRing>
          <div className="min-w-0">
            <p className="bm-label-m text-bm-ink">CacheScore&trade;</p>
            {hasScore ? (
              confidence && <p className="mt-0.5 bm-body-s text-bm-muted">Confidence: {confidence}</p>
            ) : (
              <p className="mt-0.5 bm-body-s text-bm-muted">{noScoreLabel}</p>
            )}
          </div>
        </div>
      </div>

      {/* Desktop: taller photo, hover or keyboard focus shows the CacheScore disc */}
      <div
        className={`relative hidden aspect-[2/3] w-full overflow-hidden rounded-[12px] border border-bm-border bg-bm-mutedbg lg:block ${focus}`}
        {...cardProps(hasScore && hoverScore)}
        onMouseEnter={() => { if (hoverScore) setHovered(true); }}
        onMouseLeave={() => { if (hoverScore) setHovered(false); }}
        onFocus={(e) => { if (hoverScore && isKeyboardFocus(e.currentTarget)) setFocused(true); }}
        onBlur={() => setFocused(false)}
      >
        <img src={photo} alt={alt} loading="lazy" className="h-full w-full object-cover" />
        {showLock && <LockBadge />}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[5] h-[45%] bg-gradient-to-t from-black/65 via-black/25 to-transparent" />
        {info(false)}

        {/* The V2 fade (bm-fade-in) brings the disc in; it leaves at once. */}
        {showDisc && (
          <div className="bm-fade-in pointer-events-none absolute inset-0 z-10">
            <div className="absolute inset-x-0 top-0 z-[2] flex h-[80%] items-center justify-center">
              <div className="flex w-full flex-col items-center">
                {hasScore ? (
                  <div className={DISC}>
                    <div className="bm-ring h-[92%] w-[92%]">
                      <ScoreRing value={score} stroke={7} className="h-full w-full">
                        {/* Text sizes follow the ring (.bm-ring in brandMode.css). */}
                        <div className="bm-ring-stack">
                          <div className="bm-ring-label">CacheScore&trade;</div>
                          <div className="bm-ring-score">{score}</div>
                          {confidence && (
                            <>
                              <div className="bm-ring-sub">Confidence</div>
                              <div className="bm-ring-level">{confidence}</div>
                            </>
                          )}
                        </div>
                      </ScoreRing>
                    </div>
                  </div>
                ) : (
                  <div className={`${DISC} flex-col`}>
                    <ScoreRing value={null} stroke={9} className="mb-2 h-10 w-10" />
                    <p className="bm-h-s text-bm-ink">CacheScore&trade;</p>
                    <p className="mt-0.5 bm-body-m text-bm-muted">{noScoreLabel}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

// The school and team a signed-in Player Index row names: the server's
// labels from the athlete's identity (schoolLabel: the institution their
// profile leads with, teamLabel: the team they are on), so a college a team
// link filed in the Club field reads as the college. A row from before the
// labels resolves the codes as the card did.
export const rowSchoolAndTeam = (athlete = {}, codeMap = {}) => {
  if (athlete.schoolLabel != null || athlete.teamLabel != null) {
    return { school: athlete.schoolLabel || '', team: athlete.teamLabel || '' };
  }
  return {
    school: codeMap.school?.[athlete.previousSchool]?.name || '',
    team: codeMap.team?.[athlete.currentTeam]?.name || '',
  };
};

// Signed-in Player Index card: the list row carries codes (the sport and
// state resolved here) and its school and team labels.
const AllAthleteCard = ({ athlete, codes, onClick, detailRequired, isHoverRequired = false, showLock = false }) => {
  const codeMap = useCodeMaps(codes);
  if (!athlete || Object.keys(athlete).length === 0) return null;

  const primarySportName = codeMap.sport?.[athlete.primarySport]?.name || '';
  const { school: schoolName, team: currentTeamName } = rowSchoolAndTeam(athlete, codeMap);
  const stateName = codeMap.state?.[athlete.state]?.name || '';
  const fullName = [athlete.name, athlete.surname].filter(Boolean).join(' ');

  return (
    <PlayerIndexCard
      name={fullName}
      avatar={athlete.avatar}
      alt={`Avatar of ${fullName}`}
      sport={primarySportName !== '-' ? primarySportName : ''}
      jersey={athlete.jerseyNumber}
      // School first, team as fallback, then the formatted hometown the
      // row carries, else the state name from the code.
      meta={cardMetaLine(schoolName || currentTeamName, cardPlace(athlete, stateName))}
      score={athlete.cacheScoreValue || null}
      confidence={getConfidenceLabel(athlete.cacheScoreConfidenceFactor)}
      noScoreLabel="Coming soon"
      hoverScore={!!(isHoverRequired && detailRequired)}
      showLock={showLock}
      onClick={onClick}
    />
  );
};

export default AllAthleteCard;
